import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTeamDetail } from '../features/team';
import type { TeamStatsDTO } from '../features/team';

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function StatItem({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-baseline justify-between py-1.5">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-sm text-gray-900 tabular-nums font-medium">{value}</span>
    </div>
  );
}

function StatsSection({ title, stats }: { title: string; stats: TeamStatsDTO }) {
  return (
    <div className="border border-gray-200 rounded-md">
      <div className="px-4 py-2.5 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-medium text-gray-700">{title}</h3>
      </div>
      <div className="px-4 py-2 grid grid-cols-2 lg:grid-cols-3 gap-x-8">
        <StatItem label="直邀人数" value={stats.direct_invite_count} />
        <StatItem label="团队总人数" value={stats.team_count} />
        <StatItem label="一级邀请数" value={stats.level1_count} />
        <StatItem label="二级邀请数" value={stats.level2_count} />
        <StatItem label="三级邀请数" value={stats.level3_count} />
        <StatItem label="首佣人数" value={stats.first_commission_count} />
        <StatItem label="一级返佣" value={`¥${stats.level1_commission_yuan}`} />
        <StatItem label="二级返佣" value={`¥${stats.level2_commission_yuan}`} />
        <StatItem label="三级返佣" value={`¥${stats.level3_commission_yuan}`} />
        <StatItem label="任务奖励总额" value={`¥${stats.total_task_reward_yuan}`} />
        <StatItem label="返佣总额" value={`¥${stats.total_commission_yuan}`} />
        <StatItem label="统计更新时间" value={formatDateTime(stats.updated_at)} />
      </div>
    </div>
  );
}

export function TeamPage() {
  const { data, loading, error, query, retry } = useTeamDetail();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUserId = searchParams.get('userId') || '';
  const [inputValue, setInputValue] = useState(initialUserId);

  const handleQuery = useCallback(() => {
    const id = parseInt(inputValue, 10);
    if (!id || id <= 0) return;
    setSearchParams({ userId: String(id) }, { replace: true });
    query(id);
  }, [inputValue, query, setSearchParams]);

  useEffect(() => {
    const id = parseInt(initialUserId, 10);
    if (id > 0) {
      setInputValue(String(id));
      query(id);
    }
  }, [initialUserId, query]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleQuery();
  }, [handleQuery]);

  const inputValid = /^\d+$/.test(inputValue) && parseInt(inputValue, 10) > 0;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">输入用户 ID 查询其团队结构和返佣统计。</p>

      {/* Query bar */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value.replace(/\D/g, ''))}
          onKeyDown={handleKeyDown}
          placeholder="用户 ID"
          className="h-9 w-40 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <button
          onClick={handleQuery}
          disabled={!inputValid || loading}
          className="h-9 px-4 text-sm bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-40 transition-colors"
        >
          {loading ? '查询中...' : '查询'}
        </button>
        <button
          onClick={() => {
            setInputValue('');
            setSearchParams({}, { replace: true });
          }}
          className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          清空
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="py-10 text-center">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button onClick={retry} className="text-sm text-primary hover:underline">重试</button>
        </div>
      )}

      {/* Empty guide */}
      {!data && !loading && !error && (
        <div className="py-16 text-center text-sm text-gray-400">
          输入用户 ID 并点击"查询"或按回车键，查看该用户的团队信息。
        </div>
      )}

      {/* Result */}
      {data && !loading && (
        <div className="space-y-4">
          {/* User info */}
          <div className="border border-gray-200 rounded-md">
            <div className="px-4 py-2.5 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-700">用户信息</h3>
            </div>
            <div className="px-4 py-3 grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1">
              <StatItem label="用户 ID" value={data.user.id} />
              <StatItem label="邮箱" value={data.user.email} />
              <StatItem label="昵称" value={data.user.nickname || '-'} />
              <StatItem label="角色" value={data.user.role} />
              <StatItem label="状态" value={data.user.status} />
              <StatItem label="邀请码" value={data.user.invite_code || '-'} />
            </div>
          </div>

          {/* Team stats */}
          {data.team_stats ? (
            <StatsSection title="团队统计" stats={data.team_stats} />
          ) : (
            <div className="py-10 text-center text-sm text-gray-400 border border-gray-200 rounded-md">
              该用户暂无团队统计数据
            </div>
          )}
        </div>
      )}
    </div>
  );
}
